/**
 * Plans, feature matrix, add-ons and enterprise capabilities.
 *
 * This is the single source of truth for every price and every per-plan claim on
 * dedupr.cloud. It feeds three surfaces — the landing page's pricing cards, the
 * dedicated `#/pricing` page, and the Ask Dedupr assistant's system prompt
 * (worker/knowledge.ts) — for the same reason `data/connectors.ts` does: a price
 * that lives in three places is a price that will eventually differ in three
 * places, and a customer who is quoted one number by the chat and another by the
 * page has caught us being careless with the one thing they care most about.
 *
 * Every row of FEATURE_GROUPS reproduces a row of docs/landing/pricing.md §2. Do
 * not paraphrase a cell, drop a row, or add one that the doc does not have.
 *
 * Two conventions worth knowing before you edit:
 *
 * 1. **Prices are numerals without a currency.** Local pricing is symbol-swap:
 *    the same number in USD, GBP and EUR (owner decision, 2026-07-30) — real
 *    local prices rather than FX conversions, which is what pricing.md §8.4 asks
 *    for. Renderers prefix `useCurrency().symbol`. There is deliberately no
 *    per-currency price table to drift.
 * 2. **Annual is derived, never stored.** `annualPrice()` is twelve months less
 *    two (pricing.md §2: "Annual prepay = 2 months free"). Storing both invites
 *    them to disagree.
 *
 * One documented contradiction is resolved here on the owner's instruction:
 * pricing.md §2 marks local-first (PWA) mode as Scale-and-Enterprise-only, while
 * ADR-041 and platform/planes.md §1a make it the Entry/Sandbox tier — the free
 * sandbox a buyer runs on their own data before granting any cloud access, and
 * the thing platform/planes.md routes an `entry_sandbox` tenant to. It is free on
 * every plan, and it is NOT a column in the comparison matrix: its limits are
 * architectural rather than commercial, so it gets its own section (see PWA).
 *
 * ⚠️ **The free audit is NOT a tier, and was one until 2026-08-10.** `TIERS` carried
 * `{ id: 'audit', price: 0 }` as rung zero, which put TWO free things side by side on
 * the same ladder — the local sandbox at `{sym}0` and the audit at `{sym}0` — and made
 * the audit a full column across all 21 rows of a matrix whose other columns are things
 * you can buy. Only one of them is a plan. The audit is a **motion**: a read-only
 * findings run reached from the "Free data audit" button on the home page, on the
 * `free_audit` subscription row a trial creates. It is described by `FREE_AUDIT` below,
 * which is what the modal, the Modes card and the assistant all read.
 *
 * Do not restore it to `TIERS` as a "missing tier". Two consequences to know:
 *  - No tier is zero-priced any more, so a `price === 0` branch in a renderer is dead
 *    code. `plans.test.ts` asserts the absence rather than trusting this comment.
 *  - `packages/config` still has `free_audit` in `PlanTier`, `PLAN_LADDER` and
 *    `REGIONS_INCLUDED`, and the database still enforces its region allowance. That is
 *    correct: it is what a self-serve trial's subscription row carries. It is internal,
 *    and this file is what the public is shown — `config.test.ts` reads this file as
 *    text and asserts the omission is deliberate.
 */

/** Which handler a plan's call-to-action should invoke. */
export type PlanCtaAction = 'audit' | 'booking' | 'pwa';

export interface Tier {
  id: string;
  name: string;
  /** Monthly price as a bare numeral; null where the price is not a number. */
  price: number | null;
  /** Rendered instead of a price when `price` is null, or appended when set. */
  priceNote?: string;
  /** True where the price is a floor rather than the price. */
  from?: boolean;
  blurb: string;
  ceiling: string;
  systems: string;
  cadence: string;
  users: string;
  /**
   * Included **data regions** — one D1 per (client, region) per ADR-068, so this is a
   * count of databases, not a list of places we operate.
   *
   * Added 2026-07-30. It was missing while the allowance was already enforced in the
   * control plane (`enforce_region_allowance()`) and published in pricing.md §3a — so
   * the number that decides a tier for any client with a multi-region estate was
   * absent from the page where they choose one. A test in `packages/config` asserts
   * these strings against `REGIONS_INCLUDED`, which is itself asserted against the
   * migration's SQL, so the four copies cannot drift.
   *
   * ⚠️ Phrased as "included", never "up to": beyond the allowance a region is
   * PURCHASABLE (see the add-on below), so this is not a ceiling.
   */
  regions: string;
  ctaLabel: string;
  ctaAction: PlanCtaAction;
  featured?: boolean;
}

/**
 * The four committed tiers, in ladder order. Two things are deliberately not among
 * them — see the file header: the PWA (described by `PWA`) and the free audit
 * (described by `FREE_AUDIT`). Both are free; neither is a plan.
 */
export const TIERS: Tier[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 149,
    blurb: 'One team, two systems, cleanup you can actually execute.',
    ceiling: '50k records',
    systems: '2 connected systems',
    cadence: 'daily sync',
    users: '5 users',
    regions: '1 data region',
    ctaLabel: 'Start with Starter',
    ctaAction: 'audit',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 799,
    blurb: 'Cross-system reconciliation and the audits, at hourly freshness.',
    ceiling: '250k records',
    systems: '5 connected systems',
    cadence: 'hourly sync',
    users: '15 users',
    regions: '2 data regions',
    ctaLabel: 'Start with Growth',
    ctaAction: 'audit',
    featured: true,
  },
  {
    id: 'scale',
    name: 'Scale',
    price: 2500,
    blurb: 'Unlimited systems, per-object survivorship, a daily sweep.',
    ceiling: '2M records',
    systems: 'unlimited systems',
    cadence: 'hourly sync',
    users: 'unlimited users',
    regions: '3 data regions',
    ctaLabel: 'Talk to sales',
    ctaAction: 'booking',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 4000,
    from: true,
    blurb: 'Your identity provider, your cloud, your evidence pack.',
    ceiling: '10M+ records, custom',
    systems: 'unlimited systems',
    cadence: 'hourly sync',
    users: 'unlimited users',
    regions: 'up to 5 data regions',
    ctaLabel: 'Talk to sales',
    ctaAction: 'booking',
  },
];

/** Tier ids in matrix-column order. */
export const TIER_IDS = TIERS.map((tier) => tier.id);

/** Annual price: twelve months less two (pricing.md §2). */
export function annualPrice(monthly: number): number {
  return monthly * 10;
}

/** Thousands-separated, so 7990 reads as 7,990 in every locale we sell in. */
export function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US');
}

/**
 * A matrix cell. `true`/`false` render as an included/excluded mark; a string
 * renders verbatim, which is how the doc expresses most of its cells.
 */
export type FeatureValue = boolean | string;

export interface FeatureRow {
  label: string;
  /** Values keyed by tier id. Every tier in TIER_IDS must be present. */
  values: Record<string, FeatureValue>;
}

export interface FeatureGroup {
  label: string;
  rows: FeatureRow[];
}

export const FEATURE_GROUPS: FeatureGroup[] = [
  {
    label: 'Volume and sync',
    rows: [
      {
        label: 'Buying motion',
        values: {
          starter: 'self-serve',
          growth: 'sales-assisted',
          scale: 'sales-assisted',
          enterprise: 'sales-led',
        },
      },
      {
        label: 'Connected systems',
        values: { starter: '2', growth: '5', scale: 'unlimited', enterprise: 'unlimited' },
      },
      {
        label: 'Records under management',
        values: {
          starter: '50k',
          growth: '250k',
          scale: '2M',
          enterprise: '10M+, custom',
        },
      },
      {
        label: 'Sync cadence',
        values: { starter: 'daily', growth: 'hourly', scale: 'hourly', enterprise: 'hourly' },
      },
      {
        label: 'Reconciliation sweep',
        values: { starter: 'weekly', growth: 'weekly', scale: 'daily', enterprise: 'configurable' },
      },
      {
        label: 'Users',
        values: { starter: '5', growth: '15', scale: 'unlimited', enterprise: 'unlimited' },
      },
    ],
  },
  {
    label: 'Detection and decisions',
    rows: [
      {
        label: 'Duplicate detection',
        values: {
          starter: 'exact + high-confidence fuzzy',
          growth: 'full fuzzy + custom rules',
          scale: 'plus per-object survivorship',
          enterprise: 'plus tuned thresholds',
        },
      },
      {
        label: 'Cross-system reconciliation',
        values: { starter: false, growth: true, scale: true, enterprise: true },
      },
      {
        label: 'Non-marketable savings estimate',
        values: { starter: true, growth: true, scale: true, enterprise: true },
      },
    ],
  },
  {
    label: 'Safety and reversibility',
    rows: [
      {
        label: 'Execute cleanup actions (writes)',
        values: { starter: true, growth: true, scale: true, enterprise: true },
      },
      {
        label: 'Snapshot before every write, with rollback',
        values: {
          starter: '30 days',
          growth: '30 days',
          scale: '30 days',
          enterprise: 'up to 90 days',
        },
      },
      {
        label: 'Un-merge',
        values: { starter: true, growth: true, scale: true, enterprise: true },
      },
      {
        label: 'Audit log retention',
        values: {
          starter: '1 year',
          growth: '2 years',
          scale: '2 years',
          enterprise: 'configurable',
        },
      },
    ],
  },
  {
    label: 'Delivery and AI',
    rows: [
      {
        label: 'Export to your object store',
        values: {
          starter: 'CSV download',
          growth: true,
          scale: 'yes, plus scheduled',
          enterprise: true,
        },
      },
      {
        label: 'Document and automation audits',
        values: { starter: false, growth: true, scale: true, enterprise: true },
      },
      {
        label: 'Bring your own LLM keys',
        values: { starter: true, growth: true, scale: true, enterprise: true },
      },
    ],
  },
  {
    label: 'Governance and access',
    rows: [
      {
        label: 'Data residency',
        values: {
          starter: 'EU or US',
          growth: 'EU or US',
          scale: 'EU or US',
          enterprise: 'EU, US, others on request',
        },
      },
      {
        label: 'SSO and SCIM',
        values: { starter: false, growth: false, scale: false, enterprise: true },
      },
      {
        label: 'Engine in your own cloud',
        values: { starter: false, growth: false, scale: false, enterprise: true },
      },
      {
        // Resolved against pricing.md §2 on the owner's instruction — see the
        // file header. The local sandbox is the way in, not a premium feature.
        label: 'Local-first (PWA) mode',
        values: {
          starter: 'free for everyone',
          growth: 'free for everyone',
          scale: 'free for everyone',
          enterprise: 'free for everyone',
        },
      },
    ],
  },
  {
    label: 'Support',
    rows: [
      {
        label: 'Support',
        values: {
          starter: 'email',
          growth: 'email + onboarding',
          scale: 'priority + CSM',
          enterprise: 'SLA + named CSM',
        },
      },
    ],
  },
];

export interface AddOn {
  name: string;
  /** `{sym}` is replaced with the active currency symbol at render time. */
  uplift: string;
  unlocks: string[];
  availableOn: string;
}

export const ADD_ONS: AddOn[] = [
  {
    name: 'Governed Delivery',
    uplift: '+40% of base',
    unlocks: [
      'Warehouse and lake destinations',
      'Data contracts, with row-level quarantine rather than a rejected batch',
      'An attestation per load, stating what passed and what was held',
      'Quality scored per source, object and field, tracked over time',
    ],
    availableOn: 'Starter and above',
  },
  {
    name: 'AI Access',
    uplift: '+60% of base',
    unlocks: [
      'Semantic views over your golden records, immutably versioned',
      'The identity graph as a safe projection, with structured match evidence',
      'Embeddings published to your own vector store',
      'An MCP endpoint with per-agent scoping, field-level masking, honest freshness, and an access receipt per query',
    ],
    availableOn: 'Growth and above. Fair-use query allowance; never per-query billing',
  },
  {
    name: 'Dedupr-hosted surfaces',
    uplift: 'metered',
    unlocks: [
      'A managed lake as Iceberg tables you hold read credentials to',
      'A managed vector index',
      'A managed identity-graph API',
    ],
    availableOn: 'Any plan with AI Access, if you have nowhere of your own to put them',
  },
  {
    name: 'LLM usage',
    uplift: 'provider cost + 30%',
    unlocks: [
      'Adjudication of the ambiguous match band on our keys, if you would rather not manage your own',
      'Cost receipted per decision, so the line item is auditable',
    ],
    availableOn: 'Any plan. Bring your own keys instead and there is no markup at all',
  },
  {
    name: 'Extra records',
    uplift: '{sym}60 per additional 100k per month',
    unlocks: ['Headroom past your ceiling without a tier change', 'Capped at one tier-step before we ask you to upgrade'],
    availableOn: 'Starter and above',
  },
  {
    /* Rewritten 2026-07-30 for ADR-068. The previous entry priced this 'on request'
       and offered it on Enterprise only, describing the superseded one-region-per-
       client model — under ADR-068 the count is a plan ENTITLEMENT (see each tier's
       `regions`) and an extra beyond the allowance is purchasable on any paid tier.

       ⚠️ Two different things were also conflated here and are now separated. This
       add-on is **another data plane inside the regions we already run (EU or US)**.
       A region we do not yet operate in — APAC, say — is data-residency.md §1's
       on-demand path, which is a jurisdiction question rather than a line item. */
    name: 'Additional data region',
    // A percentage, not a currency amount: every other add-on here uses `+N% of
    // base`, which is FX-neutral by construction — and this add-on's buyers are
    // definitionally multi-currency, since wanting a second region usually means
    // operating in a second economy.
    uplift: '+25% of base per region beyond your plan',
    unlocks: [
      'A second (or third) dedicated database, pinned to where those records came from',
      'Records stay in the region they originated in, rather than all moving to your first one',
    ],
    availableOn: 'Starter and above',
  },
];

export interface EnterpriseCapability {
  capability: string;
  description: string;
}

export const ENTERPRISE: EnterpriseCapability[] = [
  {
    capability: 'Single sign-on',
    description:
      'OIDC against Entra ID, Okta or Google Workspace. Turn on "SSO required" for your organisation and password sign-in is disabled.',
  },
  {
    capability: 'Multi-factor authentication',
    description:
      'Enforced through your own identity provider, where your policy already lives, rather than as a second thing for your security team to configure and audit.',
  },
  {
    capability: 'SCIM provisioning',
    description:
      'Accounts created and removed from your directory. A deprovisioned user loses their sessions within minutes rather than at the next access review.',
  },
  {
    capability: 'Custom roles and scoping',
    description:
      'Define your own permission sets, and scope a role to specific connected systems rather than to the whole workspace.',
  },
  {
    capability: 'IP allow-listing',
    description: 'Restrict access to your corporate network ranges.',
  },
  {
    capability: 'SOC 2-ready evidence packs',
    description:
      'A period-scoped export of controls plus the evidence for each, with a signed integrity manifest anchored to an external timestamping authority — so tamper-evidence does not rest on trusting our signature alone.',
  },
  {
    capability: 'AI decision receipts',
    description:
      'Every AI-influenced decision carries an immutable, PII-free record: which prompt version, which model, in which region, what it decided, what it cost, and which human approved the change.',
  },
  {
    capability: 'Records of processing and a sub-processor register',
    description:
      'Generated from your live configuration, so it lists only the AI providers you actually enabled. Bring your own keys and you can prove no Dedupr-side AI sub-processing at all.',
  },
  {
    capability: 'Data processing agreement',
    description: 'DPA on file, with the residency commitment written into it.',
  },
  {
    capability: 'The engine in your own cloud',
    description:
      'The same engine container deployed into your AWS, GCP or Azure VPC, over an outbound-only control channel. No inbound rule into your network, and no record content leaving your perimeter.',
  },
  {
    capability: 'A region we do not run yet',
    /* Narrowed 2026-07-30 for ADR-068. "Additional data regions … pinned per
       organisation" was the scalar model: it read as though holding more than one
       region were an Enterprise capability, when the count is now a plan entitlement
       on every tier. What genuinely remains Enterprise-and-on-request is a
       JURISDICTION we do not operate in yet (data-residency.md §1). */
    description:
      'A jurisdiction beyond EU and US — APAC, for instance. Additional regions within EU and US are a plan entitlement on every tier, not an Enterprise capability; this is about opening a new one.',
  },
  {
    capability: '90-day rollback',
    description: 'Extend the reversal window from 30 days to 90.',
  },
  {
    capability: 'Configurable retention and sweep cadence',
    description: 'Set your own audit-log retention and how often the reconciliation sweep re-verifies our copy.',
  },
  {
    capability: 'Tuned match thresholds',
    description: 'Thresholds calibrated against your own reviewers’ decisions rather than the defaults.',
  },
  {
    capability: 'White-label theming',
    description:
      'Your brand colour and logo throughout, including the guest remediation workspace and email. A colour that cannot meet the contrast floor is rejected rather than shipped degraded.',
  },
  {
    capability: 'A dedicated hostname',
    description: 'Your own hostname, served against resources separated from every other customer.',
  },
  {
    capability: 'Public API, webhooks and SDK',
    description:
      'Tenant-scoped API keys that can be narrower than the issuing user but never broader, HMAC-signed webhooks, and a generated TypeScript SDK over the same contract our own apps use.',
  },
  {
    capability: 'SLA and a named CSM',
    description: 'A contractual service level, and a person who knows your deployment.',
  },
];

/* ⚠️ `PWA_URL` deliberately lives in `src/config.ts`, NOT here: the Worker imports this file
   (worker/knowledge.ts) and has no `import.meta.env`, so a top-level env read here throws at
   Worker startup. Keep this module environment-free. */

/**
 * The free data audit — a **motion, not a tier** (see the file header for why it left
 * `TIERS` on 2026-08-10). This is the copy's one owner, for the same reason prices have
 * one: it is read by `AuditRequestModal` (the audit's only surface now that the pricing
 * card is gone) and by the assistant's grounding in `worker/knowledge.ts`. Two hardcoded
 * copies of the duration is how it went wrong in the first place.
 *
 * ⚠️ **`days` is 7, and the page said 30 until 2026-08-10.** The number's owner is the
 * database: `customer_start_trial(p_trial_days default 7)` creates the subscription and
 * `expire_trials()` flips `trialing → read_only` when it lapses, server-side, from the
 * control plane rather than a device clock. 30 days describes the **sales-assisted**
 * audit an operator extends — a different motion, and not what this button starts. Two
 * further copies of the wrong figure sat in `packages/config/src/regions.ts` comments.
 *
 * ⚠️ **`needsAConnectedSystem` is not a caveat to trim.** Only the records half runs on a
 * spreadsheet alone; the document and operations audits read data pulled from a live
 * system, and no vendor OAuth app exists yet (B-130). Implying otherwise is the claim
 * docs/landing/messaging.md §7 exists to prevent.
 */
export const FREE_AUDIT = {
  /** The self-serve window, in days. Enforced server-side — never a rendered guess. */
  days: 7,
  /** One line, wherever the audit is offered. Was hardcoded in three places. */
  scope: 'Two systems · 50k records · one findings report',
  looksAt: [
    'Duplicate records within each system, clustered with the evidence behind every match',
    'Contacts you are still paying to market to that have bounced, unsubscribed or never engaged',
    'Documents nobody has opened in years, near-duplicates of each other, or with no owner left',
    'Automations, sequences and connected apps that overlap, error or have gone quiet',
    'The same customer present in one system and missing from another, in both directions',
  ],
  /** What the visitor leaves with. The artefact is the point of the exercise. */
  produces:
    'One downloadable report with the formula behind every figure, so each number can be checked rather than taken on trust.',
  /** Read-only is a property of the product, not a promise: no write scope is requested. */
  readOnly:
    'Read-only throughout. Connections start with read scopes only, write permission is requested later per action type, and nothing in your systems changes.',
  needsAConnectedSystem:
    'The records half runs on a spreadsheet export alone. The document and automation halves need a connected system.',
} as const;

/**
 * The local sandbox. `notByDesign` is the honest half and the reason this has its
 * own section: each limit follows from where the code runs, so no amount of
 * building removes it. Sourced from platform/11-technical-architecture.md §6 and
 * features/15-local-first-pwa-mode.
 */
export const PWA = {
  whatItDoes: [
    /* ⚠️ **Was "compiled to WebAssembly", which describes software we did not build.** ADR-001's
       portable-engine plan and F15-FR-2 both name DuckDB-WASM; what ships is `packages/match-core`,
       TypeScript in a Web Worker over IndexedDB ([B-168b](../../../../docs/platform/TODO.md)). The
       claim that survives is the one that was always the point — it is the SAME engine as the cloud,
       not a cut-down demo — so that is what this now says. */
    'The same match engine as the cloud, running in a background thread on your own machine — not a cut-down demo',
    'Profiling, matching, clustering and survivorship all on your machine, against data staged in the browser',
    'No record content reaches our servers at all, so there is nothing to review, approve or sign before you start',
    'Sign-in to your systems is brokered by an edge proxy that only exchanges tokens, so the browser never holds a credential for your CRM',
    'A local snapshot before every write, a dry-run diff, sample-first apply, and rollback from the same device',
    'AI adjudication off entirely, relayed through us, or sent straight to your own provider endpoint — never silently anywhere',
    'One control that wipes every trace of local data',
  ],
  notByDesign: [
    {
      limit: 'Continuous monitoring',
      why: 'The device has to be awake with the app open. A closed laptop cannot watch your data drift.',
    },
    {
      limit: 'Multi-user review',
      why: 'There is no shared queue on a server, so a review is scoped to the browser profile holding it.',
    },
    {
      limit: 'Auto-merge',
      why: 'The precision gate needs a history of reviewed decisions pooled across a workspace, and one device has nowhere to pool them.',
    },
    {
      /* ⚠️ **This said "roughly 1 to 2 million records per object type" until 2026-08-08, on a
         published pricing page, while the shipped product REFUSES above 50,000.** `Modes.tsx` was
         corrected when the PWA became real and this file — the data behind the same promise — was
         not, so the page went on making the claim in a place the fix did not reach.

         ⚠️ It also evaded `lint:vocab` rule 3, which guards the retired ceiling, twice over: the
         rule's file list did not include `apps/landing`, and the figure is spelled in **words**
         rather than as `1–2M`. Both are fixed in the same change as this line.

         The real limit is not a fixed number: `capacityFor` derives it per file from the device's
         reported memory and the file's row width, and 50,000 is the published floor. Stating the
         floor is honest; stating a ceiling we never measured is the claim messaging §7 forbids. */
      limit: 'Around 50,000 records per file on a typical laptop',
      why: 'The real limit depends on your device and how wide your rows are, so the app measures it per file and tells you before it starts rather than failing part-way. 50,000 is what we are confident stating for a mid-market contact export; a bigger estate belongs in the cloud tier.',
    },
    {
      limit: 'Access from a second device',
      why: 'Each device holds an independent local dataset. Nothing syncs between them, by design.',
    },
    {
      limit: 'Document audits, automation audits, owner-led remediation and embeddings',
      why: 'These need cloud compute, and crawling a document library from a laptop is not a fair test of either.',
    },
  ],
} as const;

export interface Service {
  name: string;
  price: number;
  description: string;
}

export const SERVICES: Service[] = [
  {
    name: 'Data Audit',
    price: 2500,
    description:
      'We run the findings across your systems and walk you through what is in them, with the formula behind every figure.',
  },
  {
    name: 'Cleanup Sprint',
    price: 10000,
    description:
      'Guided cleanup end to end: we propose, you approve, and we work through the backlog with you rather than handing over a report.',
  },
];

/** Both services credit toward year-one subscription (pricing.md §4). */
export const SERVICES_CREDIT_NOTE =
  'Both credit in full toward your first year of subscription, so choosing to start with help costs nothing extra.';

export interface Faq {
  question: string;
  /** `{sym}` is replaced with the active currency symbol at render time. */
  answer: string;
}

/** Verbatim from pricing.md §7, with currency symbols left as `{sym}` tokens. */
export const FAQ: Faq[] = [
  {
    question: 'How do you count records?',
    answer:
      'Live records under management — contacts, companies, deals and documents Dedupr actively tracks. Deleted or archived records stop counting. Never per sync, per API call, or per duplicate found.',
  },
  {
    question: 'What happens if I go over?',
    answer:
      'We warn at 80%. Past the ceiling it is {sym}60 per extra 100k a month, capped at one tier-step, then we will talk about upgrading. We never stop syncing your data.',
  },
  {
    question: 'Can Dedupr change my CRM without approval?',
    answer:
      'No. Every change is proposed, previewed as a dry-run diff, and approved by a human. We snapshot before every write and you can roll back for 30 days.',
  },
  {
    question: 'Is auto-merge safe?',
    answer:
      'Auto-merge stays off until it proves at least 98% precision on your own reviewed decisions. If precision drops, the tier re-gates itself automatically.',
  },
  {
    question: 'Where is my data stored?',
    answer:
      'In the region each system\u2019s records came from, and it stays there. You pick a first region \u2014 EU or US \u2014 and your plan includes one or more; a client with an EU Salesforce and a US HubSpot gets a separate, dedicated database in each, so neither set of records leaves where it originated. Storage, processing and AI calls are all pinned per region. A jurisdiction we do not run yet is available on request.',
  },
  {
    question: 'Do you need write access?',
    answer:
      'Not at first. Every connection starts read-only; write permission is requested later, per action type, the first time you approve that kind of change.',
  },
  {
    question: 'What does the free audit include?',
    /* ⚠️ The window belongs in the answer. This stated scope and read-only and no duration
       at all, while the card beside it published "for 30 days" and the product enforced 7 —
       so the one number a prospect asks about was either absent or wrong. It comes from
       `FREE_AUDIT.days` rather than being typed here, for that exact reason. */
    answer: `One full findings run across two connected systems, up to 50k records — duplicates, non-marketable contacts with a {sym} savings estimate, and cross-system gaps. Read-only: we show you everything and change nothing. Self-serve, it runs for ${FREE_AUDIT.days} days from sign-up; after that everything already found stays readable and downloadable, and starting new work needs a plan.`,
  },
];

/** Metering rules, from pricing.md §5. `{sym}` is substituted at render time. */
export const METERING: string[] = [
  'We meter records under management — the live records we track, averaged over the day. Predictable, visible in your own CRM, and correlated with both our cost and your value.',
  'We never meter API calls. You cannot predict or control them, and surprise bills are how data tooling loses trust. Our sync also self-limits to 30% of your CRM’s daily quota by default, and never more than 50%.',
  'We never charge for a re-sync or a consistency check. Charging for the sweep that verifies our own copy would be charging you for our correctness.',
  'Overage warns at 80% of your ceiling, then runs at {sym}60 per additional 100k records a month, capped at one tier-step. We never stop syncing your data — we sit in your data flow, so a hard stop would be an outage, not a billing event.',
];
